import React from "react";
import { useNavigate } from "react-router-dom";
import { colors, fonts } from "../theme";
import { View, Text, Card, Press, ScreenHeader } from "../ui";
import { Icon } from "../Icon";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import { useAppState } from "../state";

const AVATAR = "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=300";
const MOUNTAIN = "https://images.unsplash.com/photo-1725345650770-8663568ab8d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";

function Stat({ icon, color, value, label }) {
  return (
    <View style={{ flex: 1, alignItems: "center", gap: 4 }}>
      <Icon name={icon} size={22} color={color} weight="fill" />
      <Text style={{ fontWeight: fonts.bold, fontSize: 18, color: colors.onSurface }}>{value}</Text>
      <Text style={{ fontWeight: fonts.regular, fontSize: 11, color: colors.muted, textAlign: "center" }}>{label}</Text>
    </View>
  );
}

export default function More() {
  const navigate = useNavigate();
  const { tr, lang, setLang } = useI18n();
  const { user, logout } = useAuth();
  const st = useAppState();

  const rows = [
    { icon: "note", title: tr("notes"), go: () => navigate("/notes") },
    { icon: "warning", title: tr("myMistakes"), go: () => navigate("/mistakes") },
    { icon: "bookmark", title: tr("bookmarks"), go: () => navigate("/bookmarks") },
    { icon: "lightning", title: tr("dailyReview"), go: () => navigate("/session?mode=daily") },
    { icon: "chart", title: tr("yourProgress"), go: () => navigate("/progress") },
  ];

  const logoutBtn = (
    <Press testID="logout-btn" onClick={async () => { await logout(); navigate("/welcome", { replace: true }); }} style={{
      width: 40, height: 40, borderRadius: 20, backgroundColor: colors.surface, display: "flex",
      alignItems: "center", justifyContent: "center", border: `1px solid ${colors.border}`,
    }}>
      <Icon name="sign-out" size={20} color={colors.onSurface} />
    </Press>
  );

  return (
    <View style={{ flex: 1, backgroundColor: colors.surfaceSecondary }}>
      <ScreenHeader title={tr("tabMore")} big right={logoutBtn} />
      <View style={{ padding: 20 }}>
        <Press testID="edit-profile" onClick={() => navigate("/profile")}>
          <Card style={{ flexDirection: "row", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <img alt="" src={st.state.profile.avatar || AVATAR} style={{ width: 60, height: 60, borderRadius: 30, objectFit: "cover", backgroundColor: colors.surfaceTertiary }} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontWeight: fonts.bold, fontSize: 18, color: colors.onSurface }}>{st.state.profile.name || user?.name || "Melly"}</Text>
              <Text style={{ fontWeight: fonts.regular, fontSize: 12, color: colors.muted }}>{user?.role === "admin" ? tr("admin") : tr("learner")}</Text>
              <Text numberOfLines={1} style={{ fontWeight: fonts.regular, fontSize: 12, color: colors.muted, marginTop: 2, fontStyle: "italic" }}>{st.state.profile.motto || tr("motto")}</Text>
            </View>
            <Icon name="pencil" size={18} color={colors.muted} />
          </Card>
        </Press>

        {user?.role === "admin" && (
          <Press testID="open-admin" onClick={() => navigate("/admin")}>
            <Card style={{ flexDirection: "row", alignItems: "center", gap: 12, marginBottom: 16, backgroundColor: colors.surfaceInverse, border: `1px solid ${colors.surfaceInverse}` }}>
              <View style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: "rgba(255,255,255,0.16)", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <Icon name="shield" size={22} color={colors.onSurfaceInverse} weight="fill" />
              </View>
              <View style={{ flex: 1 }}>
                <Text style={{ fontWeight: fonts.bold, fontSize: 16, color: colors.onSurfaceInverse }}>{tr("adminPanel")}</Text>
                <Text style={{ fontWeight: fonts.regular, fontSize: 12, color: "rgba(255,255,255,0.7)" }}>{tr("results")} · {tr("users")}</Text>
              </View>
              <Icon name="caret-right" size={18} color={colors.onSurfaceInverse} />
            </Card>
          </Press>
        )}

        <Card style={{ flexDirection: "row", marginBottom: 16, paddingTop: 18, paddingBottom: 18 }}>
          <Stat icon="flame" color={colors.warning} value={st.state.streak.count} label={tr("streak")} />
          <View style={{ width: 1, backgroundColor: colors.divider }} />
          <Stat icon="cards" color={colors.info} value={st.masteredCards()} label={tr("cardsMastered")} />
          <View style={{ width: 1, backgroundColor: colors.divider }} />
          <Stat icon="target" color={colors.brandPrimary} value={`${st.readiness()}%`} label={tr("examReadiness")} />
        </Card>

        <Card style={{ flexDirection: "row", alignItems: "center", marginBottom: 16 }}>
          <Icon name="chats-circle" size={22} color={colors.brandPrimary} weight="fill" />
          <Text style={{ flex: 1, fontWeight: fonts.semibold, fontSize: 15, color: colors.onSurface, marginLeft: 12 }}>{tr("language")}</Text>
          <View row style={{ backgroundColor: colors.surfaceSecondary, borderRadius: 999, padding: 3 }}>
            {["de", "en"].map((l) => (
              <div key={l} data-testid={`more-lang-${l}`} onClick={() => setLang(l)} style={{
                padding: "7px 16px", borderRadius: 999, cursor: "pointer", backgroundColor: lang === l ? colors.brandPrimary : "transparent",
              }}>
                <span style={{ fontWeight: fonts.bold, fontSize: 13, color: lang === l ? colors.onBrandPrimary : colors.muted }}>{l.toUpperCase()}</span>
              </div>
            ))}
          </View>
        </Card>

        <Card style={{ padding: 6, marginBottom: 16 }}>
          {rows.map((r, i) => (
            <Press key={i} testID={`more-row-${i}`} onClick={r.go} style={{
              display: "flex", flexDirection: "row", alignItems: "center", gap: 14, padding: 12,
              borderBottom: i < rows.length - 1 ? `1px solid ${colors.divider}` : "none",
            }}>
              <Icon name={r.icon} size={20} color={colors.brandPrimary} weight="regular" />
              <Text style={{ flex: 1, fontWeight: fonts.medium, fontSize: 15, color: colors.onSurface }}>{r.title}</Text>
              <Icon name="caret-right" size={16} color={colors.muted} />
            </Press>
          ))}
        </Card>

        <div style={{ borderRadius: 20, overflow: "hidden", height: 140, position: "relative", backgroundImage: `url(${MOUNTAIN})`, backgroundSize: "cover", backgroundPosition: "center" }}>
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(rgba(15,37,64,0.15), rgba(15,37,64,0.8))", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 18 }}>
            <Text style={{ fontWeight: fonts.bold, fontSize: 20, color: "#FFFFFF" }}>{tr("youGotThis")}</Text>
            <Text style={{ fontWeight: fonts.regular, fontSize: 13, color: "rgba(255,255,255,0.85)" }}>{tr("youGotThisSub")}</Text>
          </div>
        </div>
      </View>
    </View>
  );
}
